import type { Language } from '@eformulieren/engine'
import type { AdminOpening, AdminOpeningForExternal, Side } from '@eformulieren/model'
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { labelForExternal, labelForInternal } from './AdminQuery.js'
import { BusyButton } from './BusyButton.js'
import { ConfirmQuestion } from './ConfirmQuestion.js'
import { CountBadge, countName } from './CountBadge.js'
import { SectionPicker } from './SectionPicker.js'
import { type Scope, SectionScope } from './SectionScope.js'
import {
  type AccessRow,
  type ActionOutcome,
  type AdviceLine,
  type OwnGrant,
  type ShareLine,
  type ShareNotice,
  type ShareSection,
  adviceAbout,
  adviceStateWord,
  allSectionsNotes,
  allSectionsRightsKeeping,
  isAllSections,
  formatDay,
  grantRoleWord,
  groupAccess,
  invitedBy,
  mayChange,
  mayWithdraw,
  rightsInWords,
  roleOf,
  stateText,
  viewerIsApplicant,
} from './shareModel.js'
import { type Bilingual, formatDateTime } from '../language/text.js'
import { Spinner } from './Spinner.js'

/**
 * Het tabblad "Wie heeft toegang" (R-66, R-66a, R-66b, R-184, besluiten 2, 3, 5, 6 en 7 van
 * 30 oktober 2026, mockup 03 van delen en 04 van advies).
 *
 * Groepen Heeft toegang, Uitnodigingen en Eerder (ingeklapt), en daaronder Ingezien door NWO-beheer.
 * Bovenaan de toekenningen van de eigen kant uit het processysteem, met "Wijzigen via NWO": die komen
 * niet uit een deling en zijn hier dus ook niet te veranderen (besluit 7). Per regel het adres als de
 * pod het meestuurt, de rol in woorden, de secties bij naam (nooit een id, en geen sectie die de kijker
 * niet heeft), de stand met datum en reden, wie uitnodigde als rol, en de handelingen die de pod
 * toestaat. Een adviesregel staat alleen aan de interne kant, met de stand van het verzoek (A2, A6).
 *
 * Wat de lijst toont, levert de pod: wie via een deling binnenkwam krijgt alleen zijn eigen regel
 * (besluit 6). Het scherm veronderstelt daar niets over.
 */

export type OpeningEntry = AdminOpening | AdminOpeningForExternal

export interface AccessData {
  readonly delingen: readonly ShareLine[]
  readonly toekenningen: readonly OwnGrant[]
  readonly openingen: readonly OpeningEntry[]
  readonly advies: readonly AdviceLine[]
}

export interface AccessActions {
  /** Rechten wijzigen (`PATCH /delingen/:id`). */
  readonly onWijzigen: (id: string, rights: Readonly<Record<string, 'lezen' | 'bewerken'>>) => Promise<ActionOutcome>
  /** Intrekken, ook van een open uitnodiging (`DELETE /delingen/:id`). */
  readonly onIntrekken: (id: string) => Promise<ActionOutcome>
  /** Opnieuw uitnodigen met dezelfde rechten (een nieuwe `POST /delingen`). */
  readonly onOpnieuw: (line: ShareLine) => Promise<ActionOutcome>
  readonly onHerinneren?: (adviesId: string) => Promise<ActionOutcome>
  readonly onAdviesIntrekken?: (adviesId: string) => Promise<ActionOutcome>
  /** Het verzoek in het adviespaneel tonen; zonder deze prop staat de knop er niet. */
  readonly onBekijkAdvies?: (adviesId: string) => void
}

export interface AccessListProps extends AccessActions {
  readonly taal: Language
  readonly kant: Side
  readonly toegang: AccessData | 'laden' | 'mislukt'
  readonly secties: readonly ShareSection[]
  readonly sectionName: (sectionId: string) => string | undefined
  readonly canInvite: boolean
  /** De regel of het verzoek dat net is verstuurd: bovenaan zijn groep. */
  readonly nieuw?: string
  /** Een melding in de status van de dialoog. */
  readonly onStatus: (text: string) => void
}

const T = {
  introExtern: { nl: 'Wie aan uw kant bij dit formulier kan. Wat NWO deelt, ziet u hier niet.', en: 'Who on your side can access this form. What NWO shares is not shown here.' },
  // De interne kant ziet de delingen van beide kanten (het overzicht in de opslag, `invitations.ts`):
  // ook een uitnodiging van de aanvrager of de gemachtigde. "De delingen van de interne kant" klopte
  // daarom niet (opnamen van delen, 31 oktober 2026).
  introIntern: {
    nl: 'Iedereen die toegang heeft of is uitgenodigd, aan beide kanten. De aanvrager ziet niet wat NWO deelt.',
    en: 'Everyone who has access or has been invited, on both sides. The applicant does not see what NWO shares.',
  },
  laden: { nl: 'Bezig met ophalen van wie toegang heeft.', en: 'Loading who has access.' },
  mislukt: { nl: 'Het overzicht kon niet worden opgehaald. Sluit dit venster en probeer het opnieuw.', en: 'The overview could not be loaded. Close this window and try again.' },
  leeg: { nl: 'Nog niemand anders heeft toegang tot dit formulier.', en: 'Nobody else has access to this form yet.' },
  toegang: { nl: 'Heeft toegang', en: 'Has access' },
  uitnodigingen: { nl: 'Uitnodigingen', en: 'Invitations' },
  eerder: { nl: 'Eerder', en: 'Earlier' },
  eerderLijst: { nl: 'Eerder', en: 'Earlier' },
  beheer: { nl: 'Ingezien door NWO-beheer', en: 'Viewed by NWO administration' },
  beheerUitleg: {
    nl: 'Functioneel beheer van NWO heeft dit formulier ingezien, alleen lezen. Per keer staan hier de datum en de reden.',
    en: 'Functional administration at NWO has viewed this form, read only. Each time is listed with its date and reason.',
  },
  beheerder: { nl: 'Functioneel beheerder', en: 'Functional administrator' },
  meldingsnummer: { nl: 'meldingsnummer', en: 'incident number' },
  tot: { nl: 'tot', en: 'until' },
  u: { nl: 'U', en: 'You' },
  doorNwo: { nl: '{r}, door NWO toegevoegd', en: '{r}, added by NWO' },
  viaNwo: { nl: 'Wijzigen via NWO', en: 'Change through NWO' },
  medeschrijver: { nl: 'Medeschrijver', en: 'Co-writer' },
  meelezer: { nl: 'Meelezer', en: 'Reader' },
  adviseurBinnen: { nl: 'Adviseur binnen NWO', en: 'Adviser within NWO' },
  adviseurBuiten: { nl: 'Adviseur buiten NWO', en: 'Adviser outside NWO' },
  termijn: { nl: 'antwoord graag voor {d}', en: 'answer preferably before {d}' },
  wijzigen: { nl: 'Rechten wijzigen', en: 'Change access' },
  intrekken: { nl: 'Intrekken', en: 'Withdraw' },
  uitnodigingIntrekken: { nl: 'Uitnodiging intrekken', en: 'Withdraw invitation' },
  opnieuw: { nl: 'Opnieuw uitnodigen', en: 'Invite again' },
  herinneren: { nl: 'Herinneren', en: 'Remind' },
  bekijk: { nl: 'Bekijk advies', en: 'View advice' },
  annuleren: { nl: 'Annuleren', en: 'Cancel' },
  opslaan: { nl: 'Rechten opslaan', en: 'Save access' },
  bezigOpslaan: { nl: 'Bezig met opslaan…', en: 'Saving…' },
  bezigIntrekken: { nl: 'Bezig met intrekken…', en: 'Withdrawing…' },
  bezigUitnodigen: { nl: 'Bezig met uitnodigen…', en: 'Inviting…' },
  bezigHerinneren: { nl: 'Bezig met herinneren…', en: 'Reminding…' },
  rechtenVan: { nl: 'Rechten van {w}', en: 'Access of {w}' },
  secties: { nl: 'Welke secties?', en: 'Which sections?' },
  kies: { nl: 'Kies de secties', en: 'Choose the sections' },
  geenSectie: {
    nl: 'Kies minstens één sectie. Wilt u alle toegang weghalen, kies dan Intrekken.',
    en: 'Choose at least one section. To remove all access, choose Withdraw.',
  },
  intrekVraag: { nl: 'Toegang intrekken?', en: 'Withdraw access?' },
  intrekGevolg: {
    nl: '{w} kan dit formulier daarna niet meer openen. Wilt u later weer delen, dan nodigt u opnieuw uit.',
    en: '{w} can no longer open this form afterwards. To share again later, send a new invitation.',
  },
  uitnodigingVraag: { nl: 'Uitnodiging intrekken?', en: 'Withdraw the invitation?' },
  uitnodigingGevolg: { nl: '{w} kan de uitnodiging daarna niet meer aannemen.', en: '{w} can no longer accept the invitation afterwards.' },
  adviesVraag: { nl: 'Adviesvraag intrekken?', en: 'Withdraw the request for advice?' },
  adviesGevolg: { nl: '{w} kan dit formulier daarna niet meer lezen en niet meer antwoorden.', en: '{w} can no longer read this form or answer afterwards.' },
  gewijzigd: { nl: 'De rechten van {w} zijn gewijzigd.', en: 'The access of {w} has been changed.' },
  ingetrokken: { nl: 'De toegang van {w} is ingetrokken.', en: 'The access of {w} has been withdrawn.' },
  opnieuwVerstuurd: { nl: 'Er is een nieuwe uitnodiging verstuurd aan {w}, met dezelfde rechten.', en: 'A new invitation has been sent to {w}, with the same access.' },
  herinnerd: {
    nl: 'De herinnering voor {w} is gevraagd. NWO stuurt de e-mail.',
    en: 'The reminder for {w} has been requested. NWO sends the email.',
  },
  adviesIngetrokken: { nl: 'De adviesvraag aan {w} is ingetrokken.', en: 'The request for advice to {w} has been withdrawn.' },
} as const satisfies Readonly<Record<string, Bilingual>>

export function AccessList(props: AccessListProps): ReactNode {
  const { taal: language, kant: side, toegang: access } = props
  const headIds = { toegang: useId(), uitnodigingen: useId(), eerder: useId(), beheer: useId() }
  if (access === 'laden')
    return (
      <p className="ef-veld__hulptekst">
        <Spinner />{' '}
        {T.laden[language]}
      </p>
    )
  if (access === 'mislukt') return <p className="ef-veld__hulptekst">{T.mislukt[language]}</p>

  // De externe kant ziet nooit advies (A6, R-66a), ook niet als een pod zich zou vergissen.
  const advice = side === 'intern' ? access.advies : []
  const groups = groupAccess({ delingen: access.delingen, toekenningen: access.toekenningen, advies: advice }, props.nieuw)
  const applicant = viewerIsApplicant(access.toekenningen)
  const empty = groups.toegang.length + groups.uitnodigingen.length + groups.eerder.length === 0
  const rowsOf = (rows: readonly AccessRow[]) =>
    rows.map((row) => <Row key={rowKey(row)} {...props} rij={row} applicant={applicant} />)

  return (
    <div className="ef-toegang">
      <p className="ef-veld__hulptekst">{side === 'intern' ? T.introIntern[language] : T.introExtern[language]}</p>
      {empty ? <p className="ef-veld__hulptekst">{T.leeg[language]}</p> : null}
      {groups.toegang.length === 0 ? null : (
        <section className="ef-toegang__groep">
          <h3 id={headIds.toegang} className="ef-toegang__kop">
            {T.toegang[language]}
          </h3>
          <ul className="ef-delingen" aria-labelledby={headIds.toegang}>
            {rowsOf(groups.toegang)}
          </ul>
        </section>
      )}
      {groups.uitnodigingen.length === 0 ? null : (
        <section className="ef-toegang__groep">
          <h3 id={headIds.uitnodigingen} className="ef-toegang__kop">
            {T.uitnodigingen[language]}
          </h3>
          <ul className="ef-delingen" aria-labelledby={headIds.uitnodigingen}>
            {rowsOf(groups.uitnodigingen)}
          </ul>
        </section>
      )}
      {groups.eerder.length === 0 ? null : (
        <details className="ef-toegang__groep ef-toegang__eerder">
          {/* Het aantal als vak naast het woord en in de naam: "Eerder: 1" (besluit van 1 november 2026). */}
          <summary className="ef-toegang__kop" aria-label={countName(T.eerder[language], [String(groups.eerder.length)])}>
            {T.eerder[language]}
            <CountBadge>{String(groups.eerder.length)}</CountBadge>
          </summary>
          <ul className="ef-delingen" aria-label={T.eerderLijst[language]}>
            {rowsOf(groups.eerder)}
          </ul>
        </details>
      )}
      {access.openingen.length === 0 ? null : <Openings taal={language} openingen={access.openingen} headId={headIds.beheer} />}
    </div>
  )
}

function rowKey(row: AccessRow): string {
  if (row.soort === 'toekenning') return `toekenning-${row.toekenning.rol}-${String(row.toekenning.isU)}`
  if (row.soort === 'regel') return row.regel.id
  return row.advies.id
}

type RowProps = AccessListProps & { readonly rij: AccessRow; readonly applicant: boolean }

type Mode = 'rust' | 'wijzigen' | 'intrekken' | 'advies-intrekken'

function Row(props: RowProps): ReactNode {
  const { rij: row, taal: language } = props
  const whoId = useId()
  if (row.soort === 'toekenning') {
    const grant = row.toekenning
    const roleText = grantRoleWord(grant.rol, language)
    return (
      <li className="ef-deling" aria-labelledby={whoId}>
        <div className="ef-deling__kop">
          <span id={whoId} className="ef-deling__wie">
            {grant.isU ? T.u[language] : roleText}
          </span>
          {grant.isU ? null : <span className="ef-deling__door">{T.viaNwo[language]}</span>}
        </div>
        <p className="ef-deling__rechten">{grant.isU ? roleText : T.doorNwo[language].replace('{r}', roleText)}</p>
      </li>
    )
  }
  return <LineRow {...props} whoId={whoId} />
}

function LineRow(props: RowProps & { readonly whoId: string }): ReactNode {
  const { rij: row, taal: language, whoId, sectionName, applicant } = props
  const line = row.soort === 'regel' ? row.regel : undefined
  const advice = row.soort === 'toekenning' ? undefined : row.advies
  const [mode, setMode] = useState<Mode>('rust')
  const [notice, setNotice] = useState<ShareNotice | undefined>(undefined)
  const [rights, setRights] = useState<Readonly<Record<string, 'lezen' | 'bewerken'>>>(line?.rechten ?? {})
  /**
   * Alle secties of Alleen bepaalde secties, zoals bij uitnodigen (opdracht van 2 november 2026, ontwerp 6.4).
   * Alle secties volgt de rol die de deling al heeft (`roleOf`); wie meer of minder schrijfrecht wil geven,
   * kiest per sectie in de lijst. De keuze opent op Alle secties als de rechten daar nu precies op staan.
   */
  const [scope, setScope] = useState<Scope>('bepaald')
  const [focusSection, setFocusSection] = useState<string | undefined>(undefined)
  const scopeId = useId()
  const listRef = useRef<HTMLDivElement>(null)
  const [saving, setSaving] = useState(false)
  const errorId = useId()
  const pickerId = useId()
  const returnTo = useRef<HTMLButtonElement | null>(null)
  const [restore, setRestore] = useState(false)
  useEffect(() => {
    if (!restore) return
    returnTo.current?.focus()
    setRestore(false)
  }, [restore])
  useEffect(() => {
    if (focusSection === undefined) return
    const boxes = Array.from(listRef.current?.querySelectorAll<HTMLInputElement>('input[type=checkbox]:not([disabled])') ?? [])
    ;(boxes.find((box) => box.id.endsWith(`-${focusSection}`)) ?? boxes[0])?.focus()
    setFocusSection(undefined)
  }, [focusSection])

  const who =
    line?.email ??
    (advice !== undefined
      ? advice.adviseurKant === 'intern'
        ? T.adviseurBinnen[language]
        : T.adviseurBuiten[language]
      : line !== undefined && roleOf(line.rechten) === 'meeschrijven'
        ? T.medeschrijver[language]
        : T.meelezer[language])

  const rightsText =
    advice !== undefined
      ? rightsInWords(Object.fromEntries(advice.gedeeld.map((id) => [id, 'lezen' as const])), sectionName, language, true)
      : line === undefined
        ? ''
        : rightsInWords(line.rechten, sectionName, language)
  const state = line === undefined ? undefined : stateText(line, applicant, language)
  const by = line === undefined ? undefined : invitedBy(line, applicant, language)
  const details = [state?.tekst, by].filter((part) => part !== undefined).join(' · ')
  const label = advice !== undefined ? adviceStateWord(advice.toestand, language) : state?.label
  const adviceDetails =
    advice === undefined
      ? undefined
      : [adviceAbout(advice.anker, language), advice.termijn === null ? undefined : T.termijn[language].replace('{d}', formatDay(advice.termijn, language))]
          .filter((part) => part !== undefined)
          .join(' · ')

  /** Voert een handeling uit; geeft bij een weigering de melding terug, zodat de aanroeper haar kan plaatsen. */
  async function act(work: () => Promise<ActionOutcome>, done: string): Promise<ShareNotice | undefined> {
    setNotice(undefined)
    const outcome = await work()
    if (outcome.soort === 'gelukt') {
      setMode('rust')
      props.onStatus(done.replace('{w}', who))
      return undefined
    }
    setNotice(outcome.melding)
    return outcome.melding
  }
  const hasShown = props.secties.some((section) => section.verborgen !== true)

  const open = advice === undefined || advice.toestand === 'gevraagd' || advice.toestand === 'beantwoord'
  const canChange = line !== undefined && advice === undefined && mayChange(line)
  const canWithdraw = line !== undefined && advice === undefined && mayWithdraw(line)
  const canInviteAgain =
    line !== undefined && advice === undefined && props.canInvite && line.email !== undefined && (line.stand === 'verlopen' || line.stand === 'geweigerd' || line.stand === 'ingetrokken')
  const withdrawWord = line?.stand === 'uitgenodigd' ? T.uitnodigingIntrekken[language] : T.intrekken[language]
  const button = (text: string, onClick: () => void, kind: 'secundair' | 'gevaar' = 'secundair') => (
    <button
      type="button"
      className="ef-actieknop"
      data-soort={kind}
      aria-label={`${text}: ${who}`}
      onClick={(event) => {
        returnTo.current = event.currentTarget
        setNotice(undefined)
        onClick()
      }}
    >
      {text}
    </button>
  )

  return (
    <li className="ef-deling" aria-labelledby={whoId} data-nieuw={props.nieuw !== undefined && (line?.id === props.nieuw || advice?.id === props.nieuw) ? 'ja' : undefined}>
      <div className="ef-deling__kop">
        <span id={whoId} className="ef-deling__wie">
          {who}
        </span>
        {label === undefined ? null : (
          <span className="ef-deling__stand" data-stand={advice?.toestand ?? line?.stand}>
            {label}
          </span>
        )}
      </div>
      {rightsText === '' ? null : <p className="ef-deling__rechten">{rightsText}</p>}
      {adviceDetails === undefined ? null : <p className="ef-deling__rechten">{adviceDetails}</p>}
      {details === '' ? null : <p className="ef-deling__door">{details}</p>}
      {mode === 'wijzigen' && line !== undefined ? (
        <div className="ef-deling__wijzigen" role="group" aria-label={T.rechtenVan[language].replace('{w}', who)}>
          <SectionScope
            taal={language}
            idBase={scopeId}
            secties={props.secties}
            value={scope}
            onChange={(next) => {
              setScope(next)
              setNotice(undefined)
            }}
            notes={allSectionsNotes(props.secties, roleOf(line.rechten), language, line.rechten)}
          />
          {scope === 'bepaald' || !hasShown ? (
            <div ref={listRef} className="ef-reikwijdte__lijst">
              <SectionPicker
                taal={language}
                idBase={pickerId}
                legend={hasShown ? T.kies[language] : T.secties[language]}
                legendVoorlezen={hasShown}
                mode="schrijven"
                secties={props.secties}
                value={rights}
                onChange={setRights}
                {...(notice === undefined ? {} : { fout: { id: errorId, tekst: notice.tekst, secties: notice.secties } })}
              />
            </div>
          ) : notice === undefined ? null : (
            <p id={errorId} role="alert" className="ef-veld__melding--hard">
              {notice.tekst}
            </p>
          )}
          <div className="ef-deling__acties">
            <button
              type="button"
              className="ef-actieknop"
              data-soort="secundair"
              disabled={saving}
              onClick={() => {
                setMode('rust')
                setNotice(undefined)
                setRights(line.rechten)
                setRestore(true)
              }}
            >
              {T.annuleren[language]}
            </button>
            <BusyButton
              className="ef-actieknop"
              data-soort="primair"
              busyText={T.bezigOpslaan[language]}
              onAction={async () => {
                const all = scope === 'alle' && hasShown
                const given = all ? allSectionsRightsKeeping(line.rechten, props.secties, roleOf(line.rechten)) : rights
                if (Object.keys(given).length === 0) {
                  setNotice({ tekst: T.geenSectie[language], secties: [] })
                  return
                }
                setSaving(true)
                try {
                  const outcome = await act(() => props.onWijzigen(line.id, given), T.gewijzigd[language])
                  if (outcome !== undefined && all && outcome.secties.length > 0) {
                    // Bij Alle secties staat er geen vinkje waar de melding aan kan hangen: open de lijst met
                    // wat er gekozen was, en zet de focus op de eerste sectie die de pod noemde (R-140).
                    setRights(given)
                    setScope('bepaald')
                    setFocusSection(outcome.secties[0] ?? '')
                  }
                } finally {
                  setSaving(false)
                }
              }}
            >
              {T.opslaan[language]}
            </BusyButton>
          </div>
        </div>
      ) : null}
      {mode === 'intrekken' && line !== undefined ? (
        <ConfirmQuestion
          kop={line.stand === 'uitgenodigd' ? T.uitnodigingVraag[language] : T.intrekVraag[language]}
          consequence={(line.stand === 'uitgenodigd' ? T.uitnodigingGevolg : T.intrekGevolg)[language].replace('{w}', who)}
          proceed={withdrawWord}
          soort="gevaar"
          cancel={T.annuleren[language]}
          busyText={T.bezigIntrekken[language]}
          onProceed={() => act(() => props.onIntrekken(line.id), T.ingetrokken[language])}
          onCancel={() => {
            setMode('rust')
            setRestore(true)
          }}
        />
      ) : null}
      {mode === 'advies-intrekken' && advice !== undefined && props.onAdviesIntrekken !== undefined ? (
        <ConfirmQuestion
          kop={T.adviesVraag[language]}
          consequence={T.adviesGevolg[language].replace('{w}', who)}
          proceed={T.intrekken[language]}
          soort="gevaar"
          cancel={T.annuleren[language]}
          busyText={T.bezigIntrekken[language]}
          onProceed={() => {
            const withdraw = props.onAdviesIntrekken
            return withdraw === undefined ? undefined : act(() => withdraw(advice.id), T.adviesIngetrokken[language])
          }}
          onCancel={() => {
            setMode('rust')
            setRestore(true)
          }}
        />
      ) : null}
      {mode === 'rust' ? (
        <div className="ef-deling__acties">
          {advice !== undefined && props.onBekijkAdvies !== undefined ? button(T.bekijk[language], () => props.onBekijkAdvies?.(advice.id)) : null}
          {advice !== undefined && advice.toestand === 'gevraagd' && props.onHerinneren !== undefined ? (
            <BusyButton
              className="ef-actieknop"
              data-soort="secundair"
              aria-label={`${T.herinneren[language]}: ${who}`}
              busyText={T.bezigHerinneren[language]}
              onAction={() => {
                const remind = props.onHerinneren
                return remind === undefined ? undefined : act(() => remind(advice.id), T.herinnerd[language])
              }}
            >
              {T.herinneren[language]}
            </BusyButton>
          ) : null}
          {advice !== undefined && open && props.onAdviesIntrekken !== undefined ? button(T.intrekken[language], () => setMode('advies-intrekken'), 'gevaar') : null}
          {canChange ? button(T.wijzigen[language], () => {
            setRights(line.rechten)
            setScope(isAllSections(line.rechten, props.secties, roleOf(line.rechten)) ? 'alle' : 'bepaald')
            setMode('wijzigen')
          }) : null}
          {canWithdraw ? button(withdrawWord, () => setMode('intrekken'), 'gevaar') : null}
          {canInviteAgain ? (
            <BusyButton
              className="ef-actieknop"
              data-soort="secundair"
              aria-label={`${T.opnieuw[language]}: ${who}`}
              busyText={T.bezigUitnodigen[language]}
              onAction={() => act(() => props.onOpnieuw(line), T.opnieuwVerstuurd[language])}
            >
              {T.opnieuw[language]}
            </BusyButton>
          ) : null}
        </div>
      ) : null}
      {notice !== undefined && mode !== 'wijzigen' ? (
        <p role="alert" className="ef-veld__melding--hard">
          {notice.tekst}
        </p>
      ) : null}
    </li>
  )
}

function isComplete(opening: OpeningEntry): opening is AdminOpening {
  return 'persoon' in opening
}

/** Het moment met datum en tijd. */
function formatMoment(moment: string, language: Language): string {
  return formatDateTime(moment, language, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Amsterdam',
  })
}

/**
 * De openingen door beheer (R-66b, R-155g). Aan de externe kant de datum en de categorie in de woorden
 * van de aanvrager; aan de interne kant ook de toelichting, het meldingsnummer en tot wanneer. De
 * persoonssleutel van de beheerder staat er niet: geen sleutel in beeld (G-15).
 */
function Openings({ taal: language, openingen: openings, headId }: { readonly taal: Language; readonly openingen: readonly OpeningEntry[]; readonly headId: string }): ReactNode {
  return (
    <section className="ef-toegang__groep" aria-labelledby={headId} data-proef="openingen-door-beheer">
      <h3 id={headId} className="ef-toegang__kop">
        {T.beheer[language]}
      </h3>
      <p className="ef-veld__hulptekst">{T.beheerUitleg[language]}</p>
      <ul className="ef-delingen" aria-labelledby={headId}>
        {openings.map((opening, index) =>
          isComplete(opening) ? (
            <li key={opening.id} className="ef-deling">
              <div className="ef-deling__kop">
                <span className="ef-deling__wie">{labelForInternal(opening.redencategorie, language)}</span>
                <time className="ef-deling__door" dateTime={opening.moment}>
                  {formatMoment(opening.moment, language)}
                </time>
              </div>
              <p className="ef-deling__rechten">{opening.toelichting}</p>
              <p className="ef-deling__door">
                {T.beheerder[language]}, {T.meldingsnummer[language]} {opening.meldingsnummer}, {T.tot[language]}{' '}
                <time dateTime={opening.tot}>{formatMoment(opening.tot, language)}</time>
              </p>
            </li>
          ) : (
            <li key={`${opening.moment}-${index}`} className="ef-deling">
              <div className="ef-deling__kop">
                <span className="ef-deling__wie">{labelForExternal(opening.redencategorie, language)}</span>
                <time className="ef-deling__door" dateTime={opening.moment}>
                  {formatDay(opening.moment, language, true)}
                </time>
              </div>
            </li>
          ),
        )}
      </ul>
    </section>
  )
}

